import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Briefcase, Clock, User, ListChecks } from 'lucide-react';
import { TaskNotificationBell } from '@/components/staff/TaskNotificationBell';
import { meTaskNotifications, meMarkTaskRead } from '@/lib/staffApi';

/**
 * Mobile-first bottom navigation for the staff PWA. Sticks to the bottom
 * of the viewport, safe-area-padded for iOS home indicators.
 */
export function StaffBottomNav() {
  const items = [
    { to: '/staff/home',     icon: Home,      label: 'Home',     testid: 'staff-nav-home' },
    { to: '/staff/projects', icon: Briefcase, label: 'Projects', testid: 'staff-nav-projects' },
    { to: '/staff/tasks',    icon: ListChecks,label: 'Tasks',    testid: 'staff-nav-tasks' },
    { to: '/staff/hours',    icon: Clock,     label: 'Hours',    testid: 'staff-nav-hours' },
    { to: '/staff/profile',  icon: User,      label: 'Profile',  testid: 'staff-nav-profile' },
  ];
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur-md"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      data-testid="staff-bottom-nav"
    >
      <div className="grid grid-cols-5 max-w-2xl mx-auto">
        {items.map(({ to, icon: Icon, label, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2.5 text-[11px] transition-colors ${
                isActive ? 'text-emerald-700' : 'text-muted-foreground hover:text-foreground'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`h-5 w-5 ${isActive ? 'fill-emerald-100' : ''}`} />
                <span className="font-medium">{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/**
 * Branded top bar — keeps the staff portal feeling like its own app.
 */
export function StaffHeader({ title, action }) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-30 bg-[#064e3b] text-white border-b border-emerald-900">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/staff/home')}
          className="flex items-center gap-2 group"
          data-testid="staff-header-brand"
        >
          <div className="h-7 w-7 rounded-md bg-emerald-800 flex items-center justify-center group-hover:bg-emerald-700 transition-colors">
            <svg viewBox="0 0 512 512" className="h-4 w-4">
              <rect x="112" y="112" width="288" height="52" rx="14" fill="#34d399" />
              <rect x="112" y="230" width="220" height="52" rx="14" fill="#10b981" />
              <rect x="112" y="112" width="52" height="288" rx="14" fill="#6ee7b7" />
            </svg>
          </div>
          <span className="font-heading font-semibold text-sm leading-none">{title || 'Flex Living Miami'}</span>
        </button>
        <div className="flex items-center gap-1">
          <div className="bg-emerald-900/40 rounded-full">
            <TaskNotificationBell
              fetcher={meTaskNotifications}
              markRead={meMarkTaskRead}
              onOpenTask={() => navigate('/staff/tasks')}
              className="text-white [&_svg]:text-white"
              testId="staff-header-task-bell"
            />
          </div>
          {action}
        </div>
      </div>
    </header>
  );
}
